import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTenantFromUrl } from '../hooks/useTenantFromUrl';
import { serviceNeedInboxPath } from '../utils/serviceNeedPath';
import api, {
  clientProviderServiceAPI,
  lookupAPI,
  clientAPI,
  emailAPI,
  referralAPI,
} from '../services/api';
import { formatDisplayDate } from '../utils/dateFormat';
import { isWaitingPlaceholderProviderLastName } from '../utils/providerPlaceholder';
import { formatProviderSiteLabelFromTeamServiceRow } from '../utils/providerDisplayLabel';
import { getEffectivePermissions, hasPermission, normalizePermissionCodes } from '../utils/permissions';
import { SIMULATE_CHANGED_EVENT } from '../services/simulate';
import ContentHeader from './ContentHeader';
import DeleteIconButton from './DeleteIconButton';
import SearchableProviderTeamOfficeSelect from './SearchableProviderTeamOfficeSelect';
import './InfoIconButton.css';
import './TeamManagement.css';
import openMailtoPreferNewTab from '../utils/openMailtoPreferNewTab';

const EMAIL_TEAM_HINT = 'If nothing opens when you click an email option, set Desktop Outlook as your default mail app. On Windows: Settings → Apps → Default apps → Email → choose Outlook (desktop).';

const SC_ONLY_INFO =
  'When checked, this referral is marked as not expecting a full provider team. You can still add providers if needed.';

function TeamManagement({ referralIdOverride, clientIdOverride }) {
  const { referralId: referralIdFromParams } = useParams();
  const referralId = referralIdOverride ?? referralIdFromParams;
  const navigate = useNavigate();
  const { tenantPath } = useTenantFromUrl();
  const [referral, setReferral] = useState(null);
  const [teamServices, setTeamServices] = useState([]);
  /** Human-readable provider+office line (list/getById row, or chosen search row). */
  const [teamOfficeDisplayLabel, setTeamOfficeDisplayLabel] = useState('');
  const [frequencies, setFrequencies] = useState([]);
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  /** Full team row being edited (incl. matched need_* fields) so the form can show delivery/delay read-only + a link. */
  const [editingService, setEditingService] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showEmailDropdown, setShowEmailDropdown] = useState(false);
  const [pendingMailtoLink, setPendingMailtoLink] = useState(null);
  const [formData, setFormData] = useState({
    provider_id: '',
    provider_site_id: '',
    frequency_id: '',
  });
  const [expandedServiceId, setExpandedServiceId] = useState(null);
  const [emailHintExpanded, setEmailHintExpanded] = useState(false);
  const [providerToFromModalOpen, setProviderToFromModalOpen] = useState(false);
  const [providerToFromText, setProviderToFromText] = useState('');
  const [providerToFromLoading, setProviderToFromLoading] = useState(false);
  const [providerToFromError, setProviderToFromError] = useState(null);
  const [modalOverlayLeft, setModalOverlayLeft] = useState(0);
  const [showScOnlyInfo, setShowScOnlyInfo] = useState(false);
  const [scOnlySaving, setScOnlySaving] = useState(false);
  const formDirtyRef = useRef(false);
  /**
   * Live /whoami permission codes (same source the API uses). View-as stores a snapshot in
   * localStorage that can stay [] if navigation happened before the async whoami completed.
   */
  const [whoamiNonce, setWhoamiNonce] = useState(0);
  const [whoamiPermissions, setWhoamiPermissions] = useState(null);

  const isClosed = referral && referral.close_date;
  const clientId = clientIdOverride ?? (referral ? referral.client_id : null);
  const canCreateTeam = hasPermission('provider_team.create');
  const canUpdateTeam = hasPermission('provider_team.update');
  const canRemoveFromTeam = hasPermission('provider_team.delete');
  const scOnlyPermCodes =
    whoamiPermissions !== null ? whoamiPermissions : normalizePermissionCodes(getEffectivePermissions());
  /**
   * SC-only flag lives on the referral but is a provider-team concept; anyone who can edit the
   * roster should be able to set it (some SC roles lack referrals.update_tracking in DB).
   */
  const canToggleScOnly =
    scOnlyPermCodes.includes('referrals.update') ||
    scOnlyPermCodes.includes('referrals.update_tracking') ||
    scOnlyPermCodes.includes('provider_team.update');

  useEffect(() => {
    const onSim = () => setWhoamiNonce((n) => n + 1);
    window.addEventListener(SIMULATE_CHANGED_EVENT, onSim);
    return () => window.removeEventListener(SIMULATE_CHANGED_EVENT, onSim);
  }, []);

  useEffect(() => {
    let cancelled = false;
    api
      .get('/whoami')
      .then((res) => {
        if (cancelled) return;
        setWhoamiPermissions(normalizePermissionCodes(res.data?.permissions));
      })
      .catch(() => {
        if (!cancelled) setWhoamiPermissions(null);
      });
    return () => {
      cancelled = true;
    };
  }, [referralId, whoamiNonce]);

  useEffect(() => {
    loadData();
  }, [referralId]);

  // Close email dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showEmailDropdown && !event.target.closest('.email-team-dropdown')) {
        setShowEmailDropdown(false);
      }
    };

    document.addEventListener('pointerdown', handleClickOutside);
    return () => {
      document.removeEventListener('pointerdown', handleClickOutside);
    };
  }, [showEmailDropdown]);

  // Constrain Provider to/from modal overlay to the main content area (so it doesn't cover the left nav)
  useLayoutEffect(() => {
    if (!providerToFromModalOpen) return;
    const measure = () => {
      const content = document.querySelector('.side-nav-layout-content');
      const left = content ? content.getBoundingClientRect().left : 0;
      setModalOverlayLeft(left);
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [providerToFromModalOpen]);

  const loadData = async () => {
    if (!referralId) return;
    try {
      setLoading(true);
      const refRes = await referralAPI.getById(referralId);
      const ref = refRes.data.data;
      if (!ref) {
        alert('Referral not found');
        navigate(tenantPath('/lists/clients'));
        return;
      }
      setReferral(ref);
      await Promise.all([
        loadTeamServices(),
        loadFrequencies(),
        ref.client_id ? loadClient(ref.client_id) : Promise.resolve(),
      ]);
    } catch (error) {
      console.error('Error loading data:', error);
      alert('Failed to load team data');
    } finally {
      setLoading(false);
    }
  };

  const loadTeamServices = async () => {
    if (!referralId) return;
    try {
      const response = await clientProviderServiceAPI.getByReferralId(referralId);
      setTeamServices(response.data.data || []);
    } catch (error) {
      console.error('Error loading team services:', error);
      setTeamServices([]);
    }
  };

  const loadFrequencies = async () => {
    try {
      const response = await lookupAPI.getFrequencies();
      setFrequencies(response.data.data || []);
    } catch (error) {
      console.error('Error loading frequencies:', error);
      setFrequencies([]);
    }
  };


  const loadClient = async (cid) => {
    if (!cid) return;
    try {
      const response = await clientAPI.getById(cid);
      setClient(response.data.data);
    } catch (error) {
      console.error('Error loading client:', error);
    }
  };

  const handleAdd = () => {
    formDirtyRef.current = false;
    setEditingService(null);
    setTeamOfficeDisplayLabel('');
    setFormData({
      provider_id: '',
      provider_site_id: '',
      frequency_id: '',
    });
    setShowAddForm(true);
    setEditingId(null);
  };

  const populateEditForm = (s) => {
    setFormData({
      provider_id: s.provider_id ? String(s.provider_id) : '',
      provider_site_id: (s.payee_site_id ?? s.provider_site_id)
        ? String(s.payee_site_id ?? s.provider_site_id)
        : '',
      frequency_id: s.frequency_id ? String(s.frequency_id) : '',
    });
  };

  const handleEdit = (service) => {
    // Archived lines are frozen (read-only). Unarchive first to edit.
    if (service.archived_at) return;
    formDirtyRef.current = false;
    setEditingId(service.id);
    setEditingService(service);
    setShowAddForm(true);
    populateEditForm(service);
    setTeamOfficeDisplayLabel(
      formatProviderSiteLabelFromTeamServiceRow(service) || 'Current office on file'
    );
    clientProviderServiceAPI
      .getById(service.id)
      .then((res) => {
        const row = res?.data?.data;
        if (row && typeof row === 'object' && !formDirtyRef.current) {
          setEditingService(row);
          populateEditForm(row);
          setTeamOfficeDisplayLabel(
            formatProviderSiteLabelFromTeamServiceRow(row) || 'Current office on file'
          );
        }
      })
      .catch((e) => {
        console.warn('Team edit: list row only after getById failed', e);
      });
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to remove this provider from the team?')) {
      return;
    }

    try {
      await clientProviderServiceAPI.delete(id);
      loadTeamServices();
    } catch (error) {
      console.error('Error deleting service:', error);
      alert('Failed to remove provider from team');
    }
  };

  const handleArchive = async (service) => {
    const archiving = !service.archived_at;
    const msg = archiving
      ? 'Archive this provider line? It stays visible for reference but is excluded from delay/change tracking and reports.'
      : 'Unarchive this provider line? It will count in delay/change tracking again.';
    if (!window.confirm(msg)) return;
    try {
      await clientProviderServiceAPI.setArchived(service.id, archiving);
      loadTeamServices();
    } catch (error) {
      console.error('Error archiving service:', error);
      alert('Failed to update the archived state');
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!referralId) return;
    try {
      const providerSiteIdRaw = formData.provider_site_id;
      const provider_site_id = (providerSiteIdRaw != null && providerSiteIdRaw !== '')
        ? parseInt(providerSiteIdRaw, 10)
        : null;
      const provider_site_id_final = (typeof provider_site_id === 'number' && !Number.isNaN(provider_site_id))
        ? provider_site_id
        : null;

      // Delivery + delay now live on the Service Need; the team form only edits the roster
      // entry (provider, office, frequency).
      const dataToSave = {
        ...(formData.provider_id && String(formData.provider_id).trim() !== ''
          ? { provider_id: String(formData.provider_id).trim() }
          : {}),
        provider_site_id: provider_site_id_final,
        payee_site_id: provider_site_id_final,
        frequency_id: formData.frequency_id ? parseInt(formData.frequency_id) : null,
      };

      if (editingId) {
        const updatePayload = {
          ...(dataToSave.provider_id ? { provider_id: dataToSave.provider_id } : {}),
          provider_site_id: dataToSave.provider_site_id,
          payee_site_id: dataToSave.payee_site_id,
          frequency_id: dataToSave.frequency_id,
        };
        await clientProviderServiceAPI.update(editingId, updatePayload);
      } else {
        await clientProviderServiceAPI.createForReferral(referralId, dataToSave);
      }

      setShowAddForm(false);
      setEditingId(null);
      setEditingService(null);
      setTeamOfficeDisplayLabel('');
      loadTeamServices();
    } catch (error) {
      console.error('Error saving service:', error);
      const errorMsg = error.response?.data?.error || 'Failed to save service';
      alert(errorMsg);
    }
  };

  const handleCancel = () => {
    formDirtyRef.current = false;
    setShowAddForm(false);
    setEditingId(null);
    setEditingService(null);
    setTeamOfficeDisplayLabel('');
    setFormData({
      provider_id: '',
      provider_site_id: '',
      frequency_id: '',
    });
  };

  // Build mailto link synchronously on frontend (preserves user gesture)
  const buildMailtoLink = (templateType) => {
    if (!client || !teamServices || teamServices.length === 0) {
      alert('Client or team data not available');
      return null;
    }

    // Exclude placeholder providers (Waiting) and archived lines from the recipient list
    const teamServicesForEmail = teamServices.filter(
      (s) => !isWaitingPlaceholderProviderLastName(s.provider_last_name) && !s.archived_at
    );

    // Collect email addresses from team services
    const emailAddresses = [];
    const includePayee = templateType === 'team-providers-payees';
    teamServicesForEmail.forEach((service) => {
      if (service.provider_email) {
        emailAddresses.push(service.provider_email);
      }
      if (includePayee && service.payee_email && service.payee_email !== service.provider_email) {
        emailAddresses.push(service.payee_email);
      }
    });

    const uniqueEmails = [...new Set(emailAddresses)].filter((e) => e);
    
    if (uniqueEmails.length === 0) {
      alert('No provider email addresses found for this team');
      return null;
    }

    // Build subject and body based on template type
    const clientName = `${client.first_name} ${client.last_name}`;
    const clientDOB = client.date_of_birth ? formatDisplayDate(client.date_of_birth) : '';
    
    let subject, body;
    
    switch (templateType) {
      case 'team-providers-payees':
        subject = `Team Update - ${clientName}`;
        body = `Dear Team,\n\nThis email is regarding ${clientName}${clientDOB ? ` (DOB: ${clientDOB})` : ''}.\n\nTeam Members:\n`;
        teamServicesForEmail.forEach((service) => {
          body += `- ${service.provider_first_name} ${service.provider_last_name} (${service.provider_type_name || service.provider_type_code})\n`;
        });
        body += `\n[Email template content will be populated here]`;
        break;
        
      case '6mo-progress-notes':
        subject = `6-Month Progress Notes Request - ${clientName}`;
        body = `Dear Team,\n\nWe are requesting 6-month progress notes for ${clientName}${clientDOB ? ` (DOB: ${clientDOB})` : ''}.\n\nPlease provide progress notes for the following team members:\n`;
        teamServicesForEmail.forEach((service) => {
          body += `- ${service.provider_first_name} ${service.provider_last_name} (${service.provider_type_name || service.provider_type_code})\n`;
        });
        body += `\n[6-month progress notes request template will be populated here]`;
        break;
        
      case 'discharge-summary':
        subject = `Discharge Summary Request - ${clientName}`;
        body = `Dear Team,\n\nWe are requesting a discharge summary for ${clientName}${clientDOB ? ` (DOB: ${clientDOB})` : ''}.\n\nPlease provide discharge summaries for the following team members:\n`;
        teamServicesForEmail.forEach((service) => {
          body += `- ${service.provider_first_name} ${service.provider_last_name} (${service.provider_type_name || service.provider_type_code})\n`;
        });
        body += `\n[Discharge summary request template will be populated here]`;
        break;
        
      default:
        subject = `Team Communication - ${clientName}`;
        body = `Regarding ${clientName}${clientDOB ? ` (DOB: ${clientDOB})` : ''}.\n\n[Email template content will be populated here]`;
    }

    const emailTo = uniqueEmails.join('; ');
    return `mailto:${emailTo}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  const handleEmailTemplate = async (templateType) => {
    setShowEmailDropdown(false);
    if (!clientId) return;
    try {
      const response = await emailAPI.sendTeamEmail(clientId, templateType);
      const result = response.data.data;
      
      console.log(`[TeamManagement] Backend response:`, result);
      
      if (result.method === 'mailto') {
        // Backend returned mailto: link with template content from database
        const mailtoLink = result.mailtoLink;
        console.log(`[TeamManagement] Using template from database, mailto link length: ${mailtoLink.length}`);

        openMailtoPreferNewTab(mailtoLink);
        // Always show a clickable fallback so user can open with a real click (e.g. if default app didn't open)
        setPendingMailtoLink(mailtoLink);
      } else {
        // Server-side email was sent
        alert('Email sent successfully!');
      }
    } catch (error) {
      // Fallback to frontend-built mailto link if API fails
      const mailtoLink = buildMailtoLink(templateType);
      if (mailtoLink) {
        try {
          const link = document.createElement('a');
          link.href = mailtoLink;
          link.click();
        } catch (e) {
          try {
            window.location.href = mailtoLink;
          } catch (e2) {
            window.open(mailtoLink, '_blank');
          }
        }
        setPendingMailtoLink(mailtoLink);
      } else {
        const errorMsg = error.response?.data?.error || error.message || 'Failed to send email. Please try again.';
        alert(errorMsg);
      }
    }
  };


  const formatDate = (dateString) => (dateString ? formatDisplayDate(dateString) : '');

  const handleScOnlyToggle = async (e) => {
    if (!referralId || !referral || isClosed || !canToggleScOnly) return;
    const checked = e.target.checked;
    const prev = !!referral.sc_only_no_service_expected;
    setReferral((r) => (r ? { ...r, sc_only_no_service_expected: checked } : r));
    try {
      setScOnlySaving(true);
      await referralAPI.patchReferralTracking(referralId, { sc_only_no_service_expected: checked });
    } catch (err) {
      setReferral((r) => (r ? { ...r, sc_only_no_service_expected: prev } : r));
      alert(err.response?.data?.error || 'Failed to update SC-only flag');
    } finally {
      setScOnlySaving(false);
    }
  };

  if (loading) {
    return <div className="loading">Loading team data...</div>;
  }

  // Delivery + delay are sourced from the matched Service Need (need_*); fall back to the legacy
  // values stored on the team row for historical rows that predate the move.
  const eff = (s) => ({
    delivery_mode: s.need_service_delivery_mode ?? s.service_delivery_mode ?? null,
    delivery_start: s.need_service_delivery_start_date ?? s.service_delivery_start_date ?? null,
    delay_start: s.need_delay_start_date ?? s.delay_start_date ?? null,
    delay_reason_code: s.need_delay_reason_code ?? s.delay_reason_code ?? null,
    delay_reason_description: s.need_delay_reason_description ?? s.delay_reason_description ?? null,
    delay_end: s.need_delay_end_date ?? s.delay_end_date ?? null,
    delay_reported_to_dhs: s.need_delay_reported_to_dhs_date ?? s.delay_reported_to_dhs_date ?? null,
    delay_comment: s.need_delay_comment ?? s.delay_comment ?? null,
  });

  const deliveryModeLabel = (mode) =>
    mode === 'IN_PERSON' ? 'In Person' : mode === 'LVV' ? 'LVV' : mode === 'BOTH' ? 'Both' : (mode || 'N/A');

  const hasDelayInfo = (s) => {
    const e = eff(s);
    return e.delay_start || e.delay_reason_code || e.delay_reason_description || e.delay_end || e.delay_reported_to_dhs || (e.delay_comment && String(e.delay_comment).trim());
  };

  const handleOpenProviderToFrom = async () => {
    setProviderToFromModalOpen(true);
    setProviderToFromError(null);
    setProviderToFromText('');
    if (!referralId) return;
    setProviderToFromLoading(true);
    try {
      const res = await referralAPI.getProviderToFromText(referralId);
      const text = res.data?.text ?? '';
      setProviderToFromText(text);
    } catch (err) {
      setProviderToFromError(err.response?.data?.error || err.message || 'Failed to load provider to/from list');
    } finally {
      setProviderToFromLoading(false);
    }
  };

  const teamActions = (
    <div className="team-header-actions">
      <button
        type="button"
        onClick={handleOpenProviderToFrom}
        className="btn-provider-to-from"
      >
        Provider to/from List
      </button>
      <div className="email-team-dropdown">
        <button
          onClick={() => setShowEmailDropdown(!showEmailDropdown)}
          className="btn-email-team"
        >
          Email Team ▼
        </button>
        <button
          type="button"
          className="email-team-hint-btn"
          onClick={() => setEmailHintExpanded((prev) => !prev)}
          aria-expanded={emailHintExpanded}
          aria-label={emailHintExpanded ? 'Hide email tip' : 'Show email tip'}
          title={EMAIL_TEAM_HINT}
        >
          ⓘ
        </button>
        {emailHintExpanded && (
          <div className="email-team-hint-popover">
            {EMAIL_TEAM_HINT}
          </div>
        )}
        {pendingMailtoLink && (
          <div className="email-team-fallback">
            <span>If your email client didn&apos;t open, </span>
            <a
              href={pendingMailtoLink}
              target="_blank"
              rel="noopener noreferrer"
              className="email-team-open-link"
              onClick={() => setPendingMailtoLink(null)}
            >
              click here to open in your default email app
            </a>
            <button type="button" className="email-team-dismiss" onClick={() => setPendingMailtoLink(null)} aria-label="Dismiss">×</button>
          </div>
        )}
        {showEmailDropdown && (
          <div className="email-dropdown-menu">
            <button
              onClick={() => handleEmailTemplate('team-providers-payees')}
              className="email-option"
            >
              Email Team (Providers and Payees)
            </button>
            <button
              onClick={() => handleEmailTemplate('6mo-progress-notes')}
              className="email-option"
            >
              Email 6mo Progress Notes Request (Providers)
            </button>
            <button
              onClick={() => handleEmailTemplate('discharge-summary')}
              className="email-option"
            >
              Email Discharge Summary Request (Providers)
            </button>
          </div>
        )}
      </div>
      {!isClosed && canCreateTeam && (
        <button onClick={handleAdd} className="btn-add">
          + Add Provider
        </button>
      )}
    </div>
  );

  return (
    <div className="team-management">
      <ContentHeader
        title={isClosed ? 'Provider Team (view only)' : 'Provider Team'}
        titleHint="Most providers are added to the team when you match from Client Service Needs. You can also add a provider manually here."
        actions={teamActions}
      >
        {referral && (
          <div className="team-header-sc-only-bar">
            <div className="team-header-sc-only-row">
              <label className="team-header-sc-label">
                <input
                  type="checkbox"
                  checked={!!referral.sc_only_no_service_expected}
                  onChange={handleScOnlyToggle}
                  disabled={scOnlySaving || isClosed || !canToggleScOnly}
                  aria-busy={scOnlySaving}
                />
                <span>SC only — no service expected</span>
              </label>
              <button
                type="button"
                className="team-header-sc-info-btn"
                onClick={() => setShowScOnlyInfo((v) => !v)}
                aria-expanded={showScOnlyInfo}
                aria-controls="team-header-sc-only-help"
                aria-label={showScOnlyInfo ? 'Hide explanation' : 'Show explanation'}
              >
                ⓘ
              </button>
            </div>
            {showScOnlyInfo && (
              <p id="team-header-sc-only-help" className="team-header-sc-help-text">
                {SC_ONLY_INFO}
              </p>
            )}
          </div>
        )}
      </ContentHeader>

      {showAddForm && !isClosed && (editingId ? canUpdateTeam : canCreateTeam) && (
        <div className="team-form">
          <h3>{editingId ? 'Edit Team Member' : 'Add Team Member'}</h3>
          <form onSubmit={handleSave}>
            <div className="form-grid">
              <div className="team-form-grid-contents-row">
                <div className="form-group team-form-provider-office-span">
                  <label htmlFor="team-form-provider-office">Provider and office *</label>
                  <SearchableProviderTeamOfficeSelect
                    id="team-form-provider-office"
                    value={formData.provider_site_id}
                    displayLabel={teamOfficeDisplayLabel}
                    onChange={({ value, label, providerId, cleared }) => {
                      formDirtyRef.current = true;
                      setFormData((prev) => ({
                        ...prev,
                        provider_site_id: value,
                        provider_id: cleared || !providerId ? '' : String(providerId),
                      }));
                      setTeamOfficeDisplayLabel(label);
                    }}
                    required
                    placeholder={
                      editingId
                        ? 'Click to search if you want to change provider or office…'
                        : 'Click and type to find a provider and office…'
                    }
                  />
                </div>

                <div className="form-group">
                  <label>Frequency</label>
                  <select
                    name="frequency_id"
                    value={formData.frequency_id}
                    onChange={(e) => { formDirtyRef.current = true; setFormData({ ...formData, frequency_id: e.target.value }); }}
                  >
                    <option value="">Select frequency...</option>
                    {frequencies.map((f) => (
                      <option key={f.id} value={String(f.id)}>
                        {f.description}
                      </option>
                    ))}
                  </select>
                </div>

              </div>

              <div className="team-form-section-divider">
                <span className="team-form-section-divider-label">Delivery &amp; delay</span>
              </div>
              {!editingService ? (
                <p className="team-form-field-hint team-form-delivery-delay-note">
                  Delivery method, delivery start, and delay information are recorded on the
                  <strong> Service Need</strong>. They show here read-only once a provider is matched.
                </p>
              ) : (
                <div className="team-form-delivery-delay-note team-readonly-block">
                  {(() => {
                    const v = eff(editingService);
                    const fmt = (d) => formatDate(d) || '—';
                    return (
                      <>
                        <dl className="team-readonly-grid">
                          <div><dt>Delivery method</dt><dd>{deliveryModeLabel(v.delivery_mode)}</dd></div>
                          <div><dt>Delivery start</dt><dd>{fmt(v.delivery_start)}</dd></div>
                          <div><dt>Delay start</dt><dd>{fmt(v.delay_start)}</dd></div>
                          <div><dt>Delay reason</dt><dd>{v.delay_reason_description || v.delay_reason_code || '—'}</dd></div>
                          <div><dt>Delay end</dt><dd>{fmt(v.delay_end)}</dd></div>
                          <div><dt>DHS reported</dt><dd>{fmt(v.delay_reported_to_dhs)}</dd></div>
                          {v.delay_comment && String(v.delay_comment).trim() && (
                            <div className="team-readonly-grid-wide"><dt>Delay comment</dt><dd>{v.delay_comment}</dd></div>
                          )}
                        </dl>
                        <p className="team-readonly-note">
                          {editingService.matched_need_id ? (
                            <>
                              Edit on the Service Need:{' '}
                              <Link
                                to={serviceNeedInboxPath(tenantPath, {
                                  csNeedId: editingService.matched_need_id,
                                  clientId: editingService.client_id,
                                  referralId: editingService.referral_id || referralId,
                                })}
                                className="notifications-view-link"
                              >
                                Open Service Need →
                              </Link>
                            </>
                          ) : (
                            <>No matched Service Need is linked to this provider yet — delivery/delay are edited on the Service Need.</>
                          )}
                        </p>
                      </>
                    );
                  })()}
                </div>
              )}
            </div>

            <div className="form-actions">
              <button type="button" onClick={handleCancel} className="btn-cancel">Cancel</button>
              <button type="submit" className="btn-save">Save</button>
            </div>
          </form>
        </div>
      )}

      <div className="team-table">
        <table>
          <thead>
            <tr>
              <th>Provider</th>
              <th>Type</th>
              <th>Location</th>
              <th>Frequency</th>
              <th>Delivery Method</th>
              <th>Start</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {teamServices.length === 0 ? (
              <tr>
                <td colSpan="7" className="no-data">No team members found. Click &quot;+ Add Provider&quot; to get started.</td>
              </tr>
            ) : (
              teamServices.map((service) => (
                <React.Fragment key={service.id}>
                  <tr
                    className={`${!isClosed && canUpdateTeam && !service.archived_at ? 'team-row-clickable' : ''}${service.archived_at ? ' team-row-archived' : ''}`.trim()}
                    role={!isClosed && canUpdateTeam && !service.archived_at ? 'button' : undefined}
                    tabIndex={!isClosed && canUpdateTeam && !service.archived_at ? 0 : undefined}
                    onClick={!isClosed && canUpdateTeam && !service.archived_at ? () => handleEdit(service) : undefined}
                    onKeyDown={!isClosed && canUpdateTeam && !service.archived_at ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleEdit(service); } } : undefined}
                  >
                    <td>
                      {service.provider_last_name}, {service.provider_first_name}
                      {service.archived_at && <span className="team-archived-badge" title="Archived — excluded from delay/change tracking and reports">Archived</span>}
                      {hasDelayInfo(service) && (
                        <button
                          type="button"
                          className="team-delay-details-toggle"
                          onClick={(e) => { e.stopPropagation(); setExpandedServiceId((id) => (id === service.id ? null : service.id)); }}
                          title="Toggle delay details"
                        >
                          {expandedServiceId === service.id ? '− Details' : '+ Details'}
                        </button>
                      )}
                    </td>
                    <td>{service.provider_type_code}</td>
                    <td>{service.provider_site_label || '—'}</td>
                    <td>{service.frequency_description || 'N/A'}</td>
                    <td>{deliveryModeLabel(eff(service).delivery_mode)}</td>
                    <td>{formatDate(eff(service).delivery_start)}</td>
                    <td>
                      <div className="team-row-actions">
                        {service.provider_email && (
                          <a
                            href={`mailto:${service.provider_email}`}
                            className="team-provider-mailto-btn"
                            title={`Email ${service.provider_email}`}
                            aria-label={`Open email to ${service.provider_email}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                              <rect x="2" y="4" width="20" height="16" rx="2" />
                              <polyline points="2,4 12,13 22,4" />
                            </svg>
                          </a>
                        )}
                        {!isClosed && canUpdateTeam && (
                          <button
                            type="button"
                            className="btn-archive-line"
                            onClick={(e) => { e.stopPropagation(); handleArchive(service); }}
                            title={service.archived_at ? 'Unarchive this line' : 'Archive this line (keep for reference, exclude from tracking)'}
                            aria-label={service.archived_at ? 'Unarchive' : 'Archive'}
                          >
                            {service.archived_at ? 'Unarchive' : 'Archive'}
                          </button>
                        )}
                        {!isClosed && canRemoveFromTeam && (
                          <DeleteIconButton className="btn-delete" onClick={(e) => { e.stopPropagation(); handleDelete(service.id); }} title="Remove" aria-label="Remove" />
                        )}
                        {!service.provider_email && (isClosed || !canRemoveFromTeam) && !(!isClosed && canUpdateTeam) && (
                          <span className="no-actions">—</span>
                        )}
                      </div>
                    </td>
                  </tr>
                  {expandedServiceId === service.id && hasDelayInfo(service) && (
                    <tr className="team-detail-row">
                      <td colSpan="7">
                        <div className="team-detail-panel">
                          <h4>Delay details <span className="team-detail-readonly-note">(from the Service Need — edit there)</span></h4>
                          <dl>
                            {eff(service).delay_start && (
                              <>
                                <dt>Delay Start</dt>
                                <dd>{formatDate(eff(service).delay_start)}</dd>
                              </>
                            )}
                            {(eff(service).delay_reason_code || eff(service).delay_reason_description) && (
                              <>
                                <dt>Delay Reason</dt>
                                <dd>{eff(service).delay_reason_description || eff(service).delay_reason_code || '—'}</dd>
                              </>
                            )}
                            {eff(service).delay_end && (
                              <>
                                <dt>Delay End</dt>
                                <dd>{formatDate(eff(service).delay_end)}</dd>
                              </>
                            )}
                            {eff(service).delay_reported_to_dhs && (
                              <>
                                <dt>DHS Reported</dt>
                                <dd>{formatDate(eff(service).delay_reported_to_dhs)}</dd>
                              </>
                            )}
                            {(eff(service).delay_comment && String(eff(service).delay_comment).trim()) && (
                              <>
                                <dt>Delay Comment</dt>
                                <dd>{eff(service).delay_comment}</dd>
                              </>
                            )}
                          </dl>
                          {service.matched_need_id && (
                            <Link
                              to={serviceNeedInboxPath(tenantPath, {
                                csNeedId: service.matched_need_id,
                                clientId: service.client_id,
                                referralId: service.referral_id || referralId,
                              })}
                              className="notifications-view-link team-detail-need-link"
                              onClick={(e) => e.stopPropagation()}
                            >
                              Open Service Need →
                            </Link>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))
            )}
          </tbody>
        </table>
      </div>

      {providerToFromModalOpen && (
        <div
          className="provider-to-from-modal-overlay"
          style={{ left: modalOverlayLeft }}
          onClick={() => setProviderToFromModalOpen(false)}
          role="dialog"
          aria-modal="true"
          aria-labelledby="provider-to-from-title"
        >
          <div className="provider-to-from-modal" onClick={(e) => e.stopPropagation()}>
            <div className="provider-to-from-modal-header">
              <h2 id="provider-to-from-title">Provider to/from List</h2>
              <button type="button" className="provider-to-from-modal-close" onClick={() => setProviderToFromModalOpen(false)} aria-label="Close">×</button>
            </div>
            <div className="provider-to-from-modal-body">
              {providerToFromLoading && <p className="provider-to-from-loading">Loading…</p>}
              {providerToFromError && <p className="provider-to-from-error">{providerToFromError}</p>}
              {!providerToFromLoading && !providerToFromError && (
                <pre className="provider-to-from-text">
                  {providerToFromText
                    ? providerToFromText.replace(/^PROVIDER TO\/FROM LIST\s*\n/i, '').trim() || '(No content)'
                    : '(No content)'}
                </pre>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default TeamManagement;
